import React, { useState, useRef, useEffect } from 'react'
import { MessageCircle, Send, Bot, User, Lightbulb, Youtube, Play, Star, Eye, X, ChevronRight } from 'lucide-react'
import OntologyTag from '../components/OntologyTag'
import { useVideoStore, ConceptVideo, getYouTubeEmbedUrl, CREDIT_PER_VIEW, MIN_WATCH_SEC } from '../store/videoStore'
import ConceptVideoModal from '../components/ConceptVideoModal'

// ── 타입 ──────────────────────────────────────────────────────────────────────
interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  classes?: string[]
  timestamp: Date
  relatedConcept?: string   // 영상 추천용 개념 키워드
  conceptLabel?: string     // 화면 표시 레이블
}

// ── 키워드 → 개념 매핑 ────────────────────────────────────────────────────────
// videoStore 의 CONCEPT_TO_STEP / getVideosByConceptTag 와 연동
const KEYWORD_CONCEPT_MAP: Array<{ keywords: string[]; conceptTag: string; label: string }> = [
  { keywords: ['기울기', 'slope', '변화율', '우상향', '우하향'],           conceptTag: 'LS05', label: '기울기(slope) 이해' },
  { keywords: ['y절편', 'y-intercept', 'y 절편'],                         conceptTag: 'LS06', label: 'x절편·y절편' },
  { keywords: ['x절편', 'x-intercept', 'x 절편'],                         conceptTag: 'LS06', label: 'x절편·y절편' },
  { keywords: ['좌표', '사분면', '순서쌍'],                                conceptTag: 'LS02', label: '순서쌍과 좌표평면' },
  { keywords: ['정비례', '원점'],                                          conceptTag: 'LS04', label: '정비례 함수' },
  { keywords: ['함수식', '식 세우기', '함수 만들기', '함수 구하기'],       conceptTag: 'LS07', label: '일차함수 식 세우기' },
  { keywords: ['그래프 그리기', '그래프를 그', '직선 그리기'],             conceptTag: 'LS09', label: '그래프 그리기' },
  { keywords: ['실생활', '택시', '요금', '온도', '저금통'],               conceptTag: 'LS11', label: '실생활 문제 모델링' },
  { keywords: ['변수', '상수', '미지수'],                                  conceptTag: 'LS01', label: '변수 이해' },
  { keywords: ['M03', '오개념', '혼동', '실수'],                          conceptTag: 'LS05', label: '기울기(slope) 이해' },
]

function detectConcept(text: string): { conceptTag: string; label: string } | null {
  for (const { keywords, conceptTag, label } of KEYWORD_CONCEPT_MAP) {
    if (keywords.some(kw => text.includes(kw))) {
      return { conceptTag, label }
    }
  }
  return null
}

// ── 퀵 프롬프트 ───────────────────────────────────────────────────────────────
const QUICK_PROMPTS = [
  '기울기와 y절편의 차이를 설명해줘',
  'y = 2x + 1의 그래프는 어떻게 생겼어?',
  '정비례 함수와 1차 함수의 차이는?',
  'y = -x + 3에서 x절편을 구해줘',
  '표에서 변화율을 계산하는 방법을 알려줘',
  '오개념 M03이 뭐야?',
]

// ── AI 응답 데이터 ─────────────────────────────────────────────────────────────
const RESPONSES: Record<string, { content: string; classes: string[] }> = {
  default: {
    content: '안녕하세요! 저는 1차 함수 AI 튜터입니다. 기울기, y절편, 그래프 그리기, 실생활 문제 등 1차 함수에 관한 모든 것을 도와드립니다. 무엇이 궁금하신가요?',
    classes: ['LinearFunction', 'LearningStep'],
  },
  기울기: {
    content: `기울기(Slope)는 x가 1 증가할 때 y가 얼마나 변하는지를 나타냅니다.\n\n📌 공식: 기울기 = (y의 변화량) ÷ (x의 변화량) = Δy/Δx\n\n예시:\n• y = 2x + 1 → 기울기 = 2 (오른쪽으로 1칸 가면 위로 2칸)\n• y = -x + 3 → 기울기 = -1 (오른쪽으로 1칸 가면 아래로 1칸)\n\n💡 기울기가 양수면 우상향, 음수면 우하향 그래프입니다!`,
    classes: ['Slope', 'ChangeRate', 'IncreasingGraph', 'DecreasingGraph'],
  },
  y절편: {
    content: `y절편(Y-Intercept)은 그래프가 y축과 만나는 점의 y좌표입니다.\n\n📌 구하는 법: x = 0을 대입하면 됩니다!\n\n예시:\n• y = 2x + 1 → x=0 대입 → y = 1 → y절편 = 1\n• y = 3x → x=0 대입 → y = 0 → y절편 = 0 (원점 통과)\n\n💡 y = ax + b에서 b가 바로 y절편입니다. a는 기울기!`,
    classes: ['YIntercept', 'LinearFunction', 'ProportionalFunction'],
  },
  '2x + 1': {
    content: `y = 2x + 1의 그래프 특징:\n\n📊 기본 정보:\n• 기울기(a) = 2 → 우상향 직선 📈\n• y절편(b) = 1 → 점 (0, 1)을 지납니다\n• x절편 = -0.5 → 점 (-0.5, 0)을 지납니다\n\n📍 주요 점들:\n• (0, 1), (1, 3), (2, 5), (-1, -1)\n\n그래프 탐색기에서 직접 확인해보세요! 🎯`,
    classes: ['LinearFunction', 'Graph', 'Slope', 'YIntercept', 'Point'],
  },
  정비례: {
    content: `정비례 함수 vs 1차 함수 비교:\n\n🔵 정비례 함수: y = ax (b = 0)\n• 원점(0, 0)을 반드시 통과\n• y/x의 비율이 일정\n• 예: y = 3x\n\n🟢 일반 1차 함수: y = ax + b (a ≠ 0)\n• b = 0이면 정비례, b ≠ 0이면 일반 1차 함수\n• 예: y = 2x + 1\n\n💡 정비례 함수 ⊂ 1차 함수\n정비례는 1차 함수의 특별한 경우입니다!`,
    classes: ['ProportionalFunction', 'LinearFunction', 'ProportionalRelationship'],
  },
  x절편: {
    content: `y = -x + 3의 x절편 구하기:\n\n📌 x절편 = 그래프가 x축과 만나는 점 → y = 0일 때\n\n풀이:\ny = 0 → 0 = -x + 3 → x = 3\n\n✅ x절편 = 3, 즉 점 (3, 0)을 지납니다.\n\n그래프:\n• y절편: (0, 3)\n• x절편: (3, 0)\n• 기울기: -1 (우하향)`,
    classes: ['LinearFunction', 'Point', 'Graph', 'Slope'],
  },
  변화율: {
    content: `표에서 변화율(기울기) 계산하는 법:\n\n📋 예시 표:\nx: 1, 2, 3, 4\ny: 3, 5, 7, 9\n\n📌 계산 방법:\n변화율 = (y의 변화량) ÷ (x의 변화량)\n= (5-3) ÷ (2-1) = 2/1 = 2\n= (7-5) ÷ (3-2) = 2/1 = 2 ✓\n\n1차 함수에서는 어떤 두 점을 잡아도 변화율이 일정합니다!\n이 함수는 y = 2x + 1입니다.`,
    classes: ['ChangeRate', 'Slope', 'Table', 'LinearFunction'],
  },
  M03: {
    content: `오개념 M03: y절편과 기울기 혼동\n\n🔴 증상: y = 2x + 5에서\n• 잘못된 답: 기울기 = 5, y절편 = 2\n• 올바른 답: 기울기 = 2, y절편 = 5\n\n💡 원인: y = ax + b에서 a와 b의 위치를 혼동\n\n🔧 교정:\ny = ax + b에서\n• a = x 앞의 계수 → 기울기\n• b = 상수항 → y절편\n\n🎯 암기법: "x 앞의 수가 기울기(a), 뒤의 수가 y절편(b)"`,
    classes: ['Misconception', 'Slope', 'YIntercept', 'Feedback'],
  },
}

function getResponse(input: string): { content: string; classes: string[] } {
  if (input.includes('기울기') && input.includes('y절편')) return RESPONSES.기울기
  if (input.includes('기울기')) return RESPONSES.기울기
  if (input.includes('y절편')) return RESPONSES.y절편
  if (input.includes('2x') && input.includes('1')) return RESPONSES['2x + 1']
  if (input.includes('정비례')) return RESPONSES.정비례
  if (input.includes('x절편') || input.includes('-x + 3')) return RESPONSES.x절편
  if (input.includes('변화율') || input.includes('표')) return RESPONSES.변화율
  if (input.includes('M03') || input.includes('오개념')) return RESPONSES.M03
  return RESPONSES.default
}

// ── 인라인 영상 플레이어 (채팅 내 축소형) ─────────────────────────────────────
function InlineMiniPlayer({
  video,
  onClose,
}: {
  video: ConceptVideo
  onClose: () => void
}) {
  const { startView, completeView } = useVideoStore(s => ({
    startView: s.startView,
    completeView: s.completeView,
  }))
  const [viewId] = useState(() => startView(video.id, 'chat_student'))
  const [elapsed, setElapsed] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [validated, setValidated] = useState(false)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const start = () => {
    if (timerRef.current) return
    setPlaying(true)
    timerRef.current = setInterval(() => {
      setElapsed(e => {
        const next = e + 1
        if (next >= MIN_WATCH_SEC && !validated) {
          completeView(viewId, next)
          setValidated(true)
        }
        return next
      })
    }, 1000)
  }
  const pause = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
    setPlaying(false)
  }
  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current) }, [])

  const embedUrl = getYouTubeEmbedUrl(video.youtubeUrl ?? '')
  const pct = Math.min(100, Math.round((elapsed / MIN_WATCH_SEC) * 100))

  return (
    <div className="mt-3 rounded-2xl border-2 border-violet-200 bg-violet-50 overflow-hidden">
      {/* 헤더 */}
      <div className="flex items-center justify-between px-3 py-2 bg-violet-100">
        <div className="flex items-center gap-2">
          <Youtube size={14} className="text-red-500" />
          <span className="text-xs font-bold text-violet-700">📌 파편 학습 — 관련 영상</span>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
          <X size={14} />
        </button>
      </div>

      {/* 영상 임베드 */}
      <div className="aspect-video bg-black">
        {embedUrl
          ? <iframe src={embedUrl} className="w-full h-full" allowFullScreen title={video.title} />
          : <div className="w-full h-full flex items-center justify-center text-white/50 text-sm">영상을 불러올 수 없습니다</div>
        }
      </div>

      {/* 시청 트래커 */}
      <div className="px-3 py-2.5 space-y-2">
        <p className="text-xs font-bold text-slate-700 leading-snug">{video.title}</p>
        <p className="text-xs text-slate-500">{video.creatorName}</p>

        {!validated ? (
          <>
            <div className="flex items-center justify-between text-xs text-violet-600">
              <span>유효 시청 진행 중</span>
              <span className="font-mono">{elapsed}s / {MIN_WATCH_SEC}s</span>
            </div>
            <div className="bg-violet-200 rounded-full h-1.5">
              <div className="h-1.5 rounded-full bg-violet-500 transition-all duration-1000" style={{ width: `${pct}%` }} />
            </div>
            <p className="text-xs text-slate-400">
              {MIN_WATCH_SEC}초 이상 시청 시 크리에이터에게{' '}
              <span className="font-bold text-violet-700">{CREDIT_PER_VIEW}원</span> 지급
            </p>
            <div className="flex gap-2">
              {!playing
                ? <button onClick={start} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-violet-500 text-white text-xs font-bold hover:bg-violet-600 transition-colors">
                    <Play size={11} fill="white" /> 시청 시작
                  </button>
                : <button onClick={pause} className="px-3 py-1.5 rounded-xl bg-slate-200 text-slate-700 text-xs font-bold hover:bg-slate-300 transition-colors">
                    ⏸ 일시정지
                  </button>
              }
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 rounded-xl px-3 py-2">
            <span className="text-sm">✅</span>
            <span className="text-xs font-bold">시청 완료! 크리에이터에게 {CREDIT_PER_VIEW}원 지급됨</span>
          </div>
        )}
      </div>
    </div>
  )
}

// ── 영상 추천 카드 (채팅 메시지 하단) ─────────────────────────────────────────
function VideoRecommendCard({
  conceptTag,
  conceptLabel,
}: {
  conceptTag: string
  conceptLabel: string
}) {
  const getVideosByConceptTag = useVideoStore(s => s.getVideosByConceptTag)
  const videos = getVideosByConceptTag(conceptTag)

  const [modalOpen, setModalOpen] = useState(false)
  const [inlineVideo, setInlineVideo] = useState<ConceptVideo | null>(null)

  if (videos.length === 0) return null

  const topVideo = videos[0]

  return (
    <>
      <div className="mt-3 rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 to-blue-50 overflow-hidden">
        {/* 타이틀 바 */}
        <div className="flex items-center gap-2 px-3 py-2 border-b border-violet-100">
          <div className="w-5 h-5 rounded-md bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center flex-shrink-0">
            <Youtube size={11} className="text-white" />
          </div>
          <span className="text-xs font-bold text-violet-700">📌 파편 학습 — 관련 영상 추천</span>
          <span className="ml-auto text-xs bg-violet-100 text-violet-600 px-1.5 py-0.5 rounded-full font-medium">
            {videos.length}개
          </span>
        </div>

        {/* 썸네일 + 정보 */}
        {inlineVideo ? (
          <InlineMiniPlayer video={inlineVideo} onClose={() => setInlineVideo(null)} />
        ) : (
          <div className="flex gap-3 p-3">
            {/* 썸네일 */}
            <div className="relative flex-shrink-0 w-28 rounded-xl overflow-hidden bg-slate-200 aspect-video">
              <img
                src={topVideo.thumbnailUrl}
                alt={topVideo.title}
                className="w-full h-full object-cover"
                onError={e => { (e.target as HTMLImageElement).src = 'https://placehold.co/160x90/e2e8f0/94a3b8?text=영상' }}
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <div className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center shadow">
                  <Play size={14} className="text-violet-600 ml-0.5" fill="currentColor" />
                </div>
              </div>
              {/* 길이 배지 */}
              <div className="absolute bottom-1 right-1 bg-black/70 text-white text-xs px-1 rounded font-mono" style={{ fontSize: 9 }}>
                {Math.floor(topVideo.durationSec / 60)}:{String(topVideo.durationSec % 60).padStart(2, '0')}
              </div>
            </div>

            {/* 정보 */}
            <div className="flex-1 min-w-0 flex flex-col justify-between">
              <div>
                <p className="text-xs font-bold text-slate-800 leading-snug line-clamp-2">{topVideo.title}</p>
                <p className="text-xs text-slate-500 mt-0.5">{topVideo.creatorName}</p>
                <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                  <span className="flex items-center gap-0.5"><Eye size={10} /> {topVideo.viewCount}</span>
                  {topVideo.ratingCount > 0 && (
                    <span className="flex items-center gap-0.5"><Star size={10} className="text-yellow-400" /> {topVideo.rating.toFixed(1)}</span>
                  )}
                </div>
              </div>

              {/* 버튼 */}
              <div className="flex gap-1.5 mt-2">
                <button
                  onClick={() => setInlineVideo(topVideo)}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-violet-500 text-white text-xs font-bold hover:bg-violet-600 transition-colors"
                >
                  <Play size={10} fill="white" /> 바로 보기
                </button>
                {videos.length > 1 && (
                  <button
                    onClick={() => setModalOpen(true)}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl border border-violet-200 text-violet-600 text-xs font-semibold hover:bg-violet-50 transition-colors"
                  >
                    전체 {videos.length}개 <ChevronRight size={10} />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 개념 링크 */}
        <div className="px-3 pb-2.5 flex items-center justify-between">
          <span className="text-xs text-slate-400">
            💡 <span className="font-semibold text-slate-600">{conceptLabel}</span> 개념 영상
          </span>
          <a
            href="/video-submit"
            className="text-xs text-violet-400 hover:text-violet-600 hover:underline"
          >
            + 영상 올리기
          </a>
        </div>
      </div>

      {/* 전체 목록 모달 */}
      {modalOpen && (
        <ConceptVideoModal
          conceptTag={conceptTag}
          conceptLabel={conceptLabel}
          viewerId="chat_student"
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  )
}

// ── 메인 컴포넌트 ─────────────────────────────────────────────────────────────
export default function ChatPage() {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'init',
      role: 'assistant',
      content: '안녕하세요! 저는 1차 함수 AI 튜터입니다. 기울기, y절편, 그래프, 실생활 문제 등 무엇이든 물어보세요! 💬\n\n관련 개념 영상이 있으면 답변 아래에 자동으로 추천해 드립니다 🎬',
      classes: ['LinearFunction', 'LearningStep'],
      timestamp: new Date(),
    }
  ])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const sendMessage = async (text: string) => {
    if (!text.trim()) return
    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
      timestamp: new Date(),
    }
    setMessages(prev => [...prev, userMsg])
    setInput('')
    setLoading(true)

    await new Promise(r => setTimeout(r, 800 + Math.random() * 400))

    const resp = getResponse(text)
    // 키워드로 관련 개념 감지
    const detected = detectConcept(text)

    const aiMsg: Message = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: resp.content,
      classes: resp.classes,
      timestamp: new Date(),
      relatedConcept: detected?.conceptTag,
      conceptLabel:   detected?.label,
    }
    setMessages(prev => [...prev, aiMsg])
    setLoading(false)
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3 bg-white">
        <MessageCircle size={20} className="text-teal-600" />
        <div>
          <h1 className="text-base font-bold text-slate-800">AI 채팅 튜터</h1>
          <p className="text-xs text-slate-500">1차 함수 학습 대화형 지원 · 관련 영상 자동 추천</p>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
          <span className="text-xs text-slate-500">연결됨</span>
        </div>
      </div>

      {/* Quick prompts */}
      <div className="px-4 py-2 border-b border-slate-100 bg-slate-50 flex flex-wrap gap-1.5">
        {QUICK_PROMPTS.map(p => (
          <button
            key={p}
            onClick={() => sendMessage(p)}
            className="text-xs px-2.5 py-1 bg-white border border-slate-200 rounded-full text-slate-600 hover:bg-primary-50 hover:border-primary-200 hover:text-primary-700 transition-all"
          >
            {p}
          </button>
        ))}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {messages.map(msg => (
          <div key={msg.id} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
            {/* 아바타 */}
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
              msg.role === 'assistant'
                ? 'bg-gradient-to-br from-teal-500 to-primary-500'
                : 'bg-slate-200'
            }`}>
              {msg.role === 'assistant'
                ? <Bot size={16} className="text-white" />
                : <User size={16} className="text-slate-500" />
              }
            </div>

            {/* 말풍선 + 영상 카드 */}
            <div className={`max-w-[78%] ${msg.role === 'user' ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
              <div className={`rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                msg.role === 'assistant'
                  ? 'bg-white border border-slate-200 text-slate-800 rounded-tl-sm'
                  : 'bg-primary-500 text-white rounded-tr-sm'
              }`}>
                {msg.content}
              </div>

              {/* 온톨로지 태그 */}
              {msg.classes && msg.classes.length > 0 && (
                <div className="flex items-center gap-1 flex-wrap">
                  <Lightbulb size={11} className="text-slate-400" />
                  {msg.classes.map(c => <OntologyTag key={c} className_name={c} size="sm" />)}
                </div>
              )}

              {/* 🎬 영상 추천 카드 — AI 응답에만, 개념 감지된 경우 */}
              {msg.role === 'assistant' && msg.relatedConcept && msg.conceptLabel && (
                <VideoRecommendCard
                  conceptTag={msg.relatedConcept}
                  conceptLabel={msg.conceptLabel}
                />
              )}

              <span className="text-xs text-slate-400">
                {msg.timestamp.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          </div>
        ))}

        {/* 로딩 */}
        {loading && (
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-primary-500 flex items-center justify-center flex-shrink-0">
              <Bot size={16} className="text-white" />
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3">
              <div className="flex gap-1 items-center">
                {[0, 150, 300].map(d => (
                  <div key={d} className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />
                ))}
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-slate-200 bg-white">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage(input)}
            placeholder="1차 함수에 대해 질문하세요... (기울기, y절편, 그래프 등)"
            className="input flex-1"
          />
          <button
            onClick={() => sendMessage(input)}
            disabled={loading || !input.trim()}
            className="btn-primary px-4 flex items-center gap-1.5 disabled:opacity-50"
          >
            <Send size={15} />
          </button>
        </div>
        <p className="text-xs text-slate-400 mt-1.5 text-center">
          💬 데모 모드 &nbsp;·&nbsp; 🎬 개념 키워드 감지 시 관련 영상 자동 추천
        </p>
      </div>
    </div>
  )
}
